// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ProjectHeadVO } from '@project/model/vo/project.vo';
import { ProjectOrderingUtils } from '@project/components/project/util/project-ordering-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { UserDataService } from '@user/service/user-data.service';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectGroupDataService } from '../../import-export/service/project-group-data.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { HtmlUtils } from '@shared/util/html-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectDataService } from '../../../service/project-data.service';

@Component({
  standalone: false,
  templateUrl: './reassign.component.html',
  selector: 'app-reassign',
})
export class ReassignComponent implements OnInit {
  @Input() itemToReassign: ProjectHeadVO | ProjectGroupVO;

  @Output() public finish = new EventEmitter<ProjectHeadVO | ProjectGroupVO>();

  public ownerName: string = '';

  public infoLabel: string = '';

  constructor(
    private bsModalRef: BsModalRef,
    private userDataService: UserDataService,
    private projectModel: ProjectModel,
    private alertHelper: AlertHelper,
    private localeResourceManager: LocaleResourceManager,
    private projectDataService: ProjectDataService,
    private projectGroupDataService: ProjectGroupDataService,
  ) {}

  public ngOnInit(): void {
    this.infoLabel =
      this.itemToReassign instanceof ProjectGroupVO
        ? 'changeProjectOwnerPopup.project_infoLabel.text'
        : 'changeProjectOwnerPopup.configuration_infoLabel.text';
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public changeOwner(): void {
    if (StringUtils.isEmpty(this.ownerName) || this.ownerName.length <= 4) {
      return;
    }
    this.userDataService
      .userExists(this.ownerName)
      .toPromise()
      .then((result) => {
        this.checkUserNameSuccess(result);
      })
      .catch((e) => {
        this.checkUserNameError();
      });
  }

  private executeChangeProjectOwner(createNewVersion: boolean): void {
    this.projectDataService
      .changeProjectOwner(this.itemToReassign.id, this.ownerName, createNewVersion)
      .toPromise()
      .then((result) => {
        this.changeProjectOwnerSuccess(result);
      })
      .catch((e) => {
        this.checkUserNameError();
      });
  }

  private executeChangeProjectGroupOwner(): void {
    this.projectGroupDataService
      .changeProjectGroupOwner(this.itemToReassign.id, this.ownerName)
      .toPromise()
      .then((result) => {
        this.alertHelper.showAlert(
          'changeProjectOwnerPopup.title.projectOwnerChanged',
          'changeProjectOwnerPopup.message.projectOwnerChanged',
          { 0: this.ownerName },
          [AlertButton.OK],
          "class-ok"
        );
        this.close();
        this.finish.emit(result);
      })
      .catch((e) => {
        this.checkUserNameError();
      });
  }

  private checkUserNameSuccess(userExists: boolean): void {
    if (userExists) {
      if (this.itemToReassign instanceof ProjectHeadVO) {
        this.alertHelper
          .showAlert(
            'changeProjectOwnerPopup.title.newVersion',
            'changeProjectOwnerPopup.message.newVersion',
            null,
            [AlertButton.YES, AlertButton.NO],
            "help"
          )
          .then((answer) => {
            if (answer === AlertButton.YES) {
              this.executeChangeProjectOwner(true);
            } else {
              this.executeChangeProjectOwner(false);
            }
          });
      }
      if (this.itemToReassign instanceof ProjectGroupVO) {
        this.executeChangeProjectGroupOwner();
      }
    } else {
      this.alertHelper.showError('global.title.error', 'changeProjectOwnerPopup.message.userNameNotFound');
    }
  }

  private changeProjectOwnerSuccess(projectHead: ProjectHeadVO): void {
    const link: string = ProjectOrderingUtils.prepareDirectLink(projectHead);

    if (this.projectModel.currentProject && projectHead.id === this.projectModel.currentProject.id) {
      this.projectModel.currentProject.projectGroup = projectHead.projectGroup;
      this.projectModel.currentProject.currentVersion = projectHead.currentVersion;
      this.projectModel.currentProject.documentVersion = projectHead.documentVersion;
      this.projectModel.currentProject.finalizationState = projectHead.finalizationState;
    }

    const message: string =
      this.localeResourceManager.getValueByKey('changeProjectOwnerPopup.message.configurationOwnerChanged', { 0: this.ownerName }) + '\n' + link;
    this.alertHelper.showAlert('changeProjectOwnerPopup.title.projectOwnerChanged', message, null, [AlertButton.OK], "class-ok").then(() => {
      // copy link to clipboard
      HtmlUtils.copyTextToClipboard(link);
    });

    this.close();
    this.finish.emit(projectHead);
  }

  private checkUserNameError(): void {
    this.alertHelper.showError('global.title.error', 'changeProjectOwnerPopup.message.unexpected_error');
  }
}
